import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import { isAdmin as checkAdmin, hasAnyRole } from '../../utils/roleUtils';
import { VarianceDetail } from '../Costing/BomVarianceReport';
import EntityLink from '../../components/Common/EntityLink';
import BackButton from '../../components/Common/BackButton';
import './ProductionOrders.css';
import { formatAppDate, localToday } from '../../utils/dateUtils';

const ViewProductionOrder = forwardRef(({ orderId, onBack, editMode = false, createMode = false, salesOrderData = null, stockReplenishmentData = null, onOrderCreated, onSave, onCancel, onEdit, initialTab = null }, ref) => {
  const navigate = useNavigate();
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const isAdmin = checkAdmin(user);
  // BOM against real (#322): same roles as GET /api/costing/production-variance
  const canSeeVariance = hasAnyRole(user, 'COSTING', 'ADMIN', 'MANAGER');
  const [order, setOrder] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasChanges, setHasChanges] = useState(false);
  const [operators, setOperators] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [loadingRequisitions, setLoadingRequisitions] = useState(false);
  const [showCreateRequisitionModal, setShowCreateRequisitionModal] = useState(false);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [requisitionItems, setRequisitionItems] = useState([]);
  const [showReleaseConfirmDialog, setShowReleaseConfirmDialog] = useState(false);
  const [orderTasks, setOrderTasks] = useState(null);
  const [selectedRequisitionId, setSelectedRequisitionId] = useState(null);
  const [selectedRequisitionDetails, setSelectedRequisitionDetails] = useState(null);
  const [loadingRequisitionDetails, setLoadingRequisitionDetails] = useState(false);
  const [returns, setReturns] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [selectedReturnId, setSelectedReturnId] = useState(null);
  const [selectedReturnDetails, setSelectedReturnDetails] = useState(null);
  const [loadingReturnDetails, setLoadingReturnDetails] = useState(false);

  const { showSuccess, showError, showWarning } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    setOrderTasks(null); // reset when order changes so guard allows re-fetch
    fetchOperators();
    if (orderId && !createMode) {
      fetchOrder();
    } else if (createMode) {
      let newOrder;
      
      if (stockReplenishmentData) {
        console.log('ViewProductionOrder: Creating for stock replenishment', stockReplenishmentData);
        // Create for stock replenishment
        newOrder = {
          po_number: `PO-${Date.now().toString(36).toUpperCase()}`,
          quotation_id: stockReplenishmentData.quote_id || null,
          sales_order_id: null, // No sales order for stock replenishment
          customer_id: null,
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          created_on: localToday(),
          planned_start_date: localToday(),
          planned_due_date: localToday(14),
          actual_start_date: null,
          actual_completion_date: null,
          priority: 'NORMAL',
          status: 'PLANNED',
          purpose: stockReplenishmentData.purpose,
          total_amount: 0,
          notes: stockReplenishmentData.notes || 'Stock replenishment order',
          lines: []
        };
      } else if (salesOrderData) {
        console.log('ViewProductionOrder: Creating from sales order', salesOrderData);
        // Create from sales order
        const totalAmount = salesOrderData.lines.reduce((sum, line) =>
          sum + (line.quantityNeeded * line.unitPrice), 0
        );

        newOrder = {
          po_number: `PO-${Date.now().toString(36).toUpperCase()}`,
          quotation_id: salesOrderData.quotationId || null,
          sales_order_id: salesOrderData.salesOrderId,
          customer_id: salesOrderData.customerId,
          customer_name: salesOrderData.customerName,
          customer_email: salesOrderData.customerEmail || '',
          customer_phone: salesOrderData.customerPhone || '',
          created_on: localToday(),
          planned_start_date: localToday(),
          planned_due_date: localToday(14),
          actual_start_date: null,
          actual_completion_date: null,
          priority: 'NORMAL',
          status: 'PLANNED',
          purpose: 'CUSTOMER_ORDER',
          total_amount: totalAmount,
          notes: `Created from Sales Order ${salesOrderData.salesOrderNumber}${salesOrderData.quotationId ? ' (originated from quotation)' : ''}`,
          lines: salesOrderData.lines.map((line, index) => ({
            line_number: index + 1,
            product_id: line.productId,
            product_code: line.productCode,
            product_name: line.productName,
            order_quantity: line.quantityNeeded,
            unit_price: line.unitPrice,
            line_total: line.quantityNeeded * line.unitPrice,
            bom_id: null,
            sales_order_line_id: line.salesOrderLineId || null,
            status: 'PENDING',
            notes: ''
          }))
        };
      } else {
        // Create empty order
        newOrder = {
          po_number: `PO-${Date.now().toString(36).toUpperCase()}`,
          quotation_id: null,
          customer_id: null,
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          created_on: localToday(),
          planned_start_date: '',
          planned_due_date: localToday(14),
          actual_start_date: null,
          actual_completion_date: null,
          priority: 'NORMAL',
          status: 'PLANNED',
          total_amount: 0,
          notes: '',
          lines: []
        };
      }
      
      setOrder(newOrder);
      setEditData(newOrder);
    }
  }, [orderId, createMode, salesOrderData, stockReplenishmentData]);

  // Apply initialTab when provided (e.g. navigating back from a task detail)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Fetch requisitions and returns when activeTab changes to 'requisitions'
  useEffect(() => {
    if (activeTab === 'requisitions' && order?.production_order_id && !createMode) {
      fetchRequisitions();
      fetchReturns();
    }
  }, [activeTab, order?.production_order_id, createMode]);

  // Fetch tasks when activeTab changes to 'tasks'
  useEffect(() => {
    if (activeTab === 'tasks' && order?.production_order_id && !createMode && orderTasks === null) {
      fetchOrderTasks();
    }
  }, [activeTab, order?.production_order_id, createMode, orderTasks]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchOperators = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/users/'));
      if (response.ok) {
        const users = await response.json();
        setOperators(users || []);
      } else {
        showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
      }
    } catch (error) {
      console.error('Error loading operators:', error);
      showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
    }
  };

  const fetchOrder = async (orderIdToFetch = null) => {
    setLoading(true);
    try {
      // Use provided ID, or current editData.po_number, or original orderId
      const fetchId = orderIdToFetch || editData?.po_number || order?.po_number || orderId;
      if (!fetchId) {
        setError(t('viewProductionOrder.errors.noOrderId'));
        setLoading(false);
        return;
      }

      const response = await apiFetch(getApiUrl(`/api/production/orders/${fetchId}`));
      if (response.ok) {
        const data = await response.json();
        // Map backend field names if needed
        const mappedData = {
          ...data,
          po_number: data.po_number || data.job_number,
          created_on: data.created_on || data.order_date,
          planned_due_date: data.planned_due_date || data.due_date,
          actual_completion_date: data.actual_completion_date || data.actual_end_date,
          items: data.lines || data.items || []  // Use lines from backend
        };
        setOrder(mappedData);
        setEditData(mappedData);

        // Also fetch lines using dedicated endpoint
        if (mappedData.po_number) {
          await fetchOrderLines(mappedData.po_number);
        }
      } else {
        setError(t('viewProductionOrder.errors.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching production order:', error);
      setError(t('viewProductionOrder.errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderLines = async (poNumber) => {
    try {
      const response = await apiFetch(getApiUrl(`/api/production/orders/${poNumber}/lines`));
      if (response.ok) {
        const data = await response.json();
        setEditData(prev => ({
          ...prev,
          items: data.lines || []
        }));
      }
    } catch (error) {
      console.error('Error fetching order lines:', error);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setError(null);

      // Require produced quantity on all lines before completing
      if (editData.status === 'COMPLETED') {
        const lines = editData.items || [];
        const unrecorded = lines.filter(item => !(parseFloat(item.produced_quantity) > 0));
        if (lines.length === 0 || unrecorded.length > 0) {
          const names = unrecorded.map(l => l.product_name || t('viewProductionOrder.lineFallback', { number: l.line_number })).join(', ');
          showError(
            t('viewProductionOrder.cannotComplete.title'),
            lines.length === 0
              ? t('viewProductionOrder.cannotComplete.noLines')
              : t('viewProductionOrder.cannotComplete.unrecordedLines', { names })
          );
          return;
        }
      }

      const url = createMode
        ? getApiUrl('/api/production/orders')
        : getApiUrl(`/api/production/orders/${editData.po_number || orderId}`);

      const method = createMode ? 'POST' : 'PUT';

      // Filter out fields that don't exist in backend
      const {
        assigned_operator_id,
        operator_name,
        items,  // Remove items from save payload
        lines,  // Remove lines from save payload
        product_type,  // Remove - not in backend
        reference_number,  // Remove - not in backend
        quantity,  // Remove - not in backend
        total_quantity,  // Remove - not in backend
        produced_quantity,  // Remove - not in backend
        defective_quantity,  // Remove - not in backend
        // special_instructions exists in backend - don't filter
        // customer_email and customer_phone are now in backend - don't filter
        ...dataToSave
      } = editData;

      // Convert empty date strings to null for backend compatibility
      if (dataToSave.actual_start_date === '') {
        dataToSave.actual_start_date = null;
      }
      if (dataToSave.actual_completion_date === '') {
        dataToSave.actual_completion_date = null;
      }
      if (dataToSave.planned_start_date === '') {
        dataToSave.planned_start_date = null;
      }

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        if (createMode) {
          // For new orders, use the returned po_number
          const newOrderId = result.po_number || result.production_order_id;
          if (newOrderId) {
            setOrder(result);
            setEditData({ ...result, items: editData.items || [] });
            if (onOrderCreated) {
              onOrderCreated(newOrderId);
            }
          }
        } else {
          // For existing orders, refresh the data using the correct ID
          const orderIdToRefresh = result.po_number || editData.po_number || orderId;
          await fetchOrder(orderIdToRefresh);
        }

        showSuccess(
          createMode ? t('messages.productionOrderCreated') : t('messages.productionOrderUpdated'),
          createMode ? t('messages.productionOrderCreatedSuccess') : t('messages.productionOrderUpdatedSuccess')
        );

        if (onSave) onSave(result);
      } else {
        await showApiError(response, showError, tCommon, createMode ? t('messages.failedToCreateOrder') : t('messages.failedToUpdateOrder'));
        return;
      }
    } catch (error) {
      console.error('Error saving production order:', error);
      setError(t('viewProductionOrder.errors.saveError'));
      showError(
        createMode ? t('messages.failedToCreateOrder') : t('messages.failedToUpdateOrder'),
        t('viewProductionOrder.errors.saveError')
      );
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(order);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleReleaseClick = () => {
    setShowReleaseConfirmDialog(true);
  };

  const handleReleaseConfirm = async () => {
    setShowReleaseConfirmDialog(false);

    try {
      const response = await apiFetch(getApiUrl(`/api/production/orders/${order.po_number}/release`), {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(t('messages.productionOrderReleased'), data.message);

        // Show details about created workflow instances
        if (data.workflow_instances && data.workflow_instances.length > 0) {
          const instanceDetails = data.workflow_instances
            .map(wi => t('viewProductionOrder.workflowTasksCreated', { product: wi.product, count: wi.tasks_created }))
            .join('\n');
          showSuccess(t('viewProductionOrder.workflowsCreated'), instanceDetails);
        }

        await fetchOrder();
      } else {
        await showApiError(response, showError, tCommon, t('messages.failedToReleaseOrder'));
      }
    } catch (error) {
      console.error('Error releasing production order:', error);
      showError(t('messages.failedToReleaseOrder'), t('viewProductionOrder.errors.releaseError'));
    }
  };

  const handleForceClose = async () => {
    if (!window.confirm(t('viewProductionOrder.forceClose.confirm'))) return;
    try {
      const response = await apiFetch(getApiUrl(`/api/production/orders/${order.po_number}/force-close`), {
        method: 'POST'
      });
      if (response.ok) {
        showSuccess(t('viewProductionOrder.forceClose.successTitle'), t('viewProductionOrder.forceClose.successMessage'));
        await fetchOrder();
      } else {
        await showApiError(response, showError, tCommon, t('viewProductionOrder.forceClose.failed'));
      }
    } catch (error) {
      console.error('Error force-closing production order:', error);
      showError(t('viewProductionOrder.forceClose.failed'), t('viewProductionOrder.errors.unexpected'));
    }
  };

  const fetchRequisitions = async () => {
    if (!order?.production_order_id) return;

    try {
      setLoadingRequisitions(true);
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions?production_order_id=${order.production_order_id}`));
      if (response.ok) {
        const data = await response.json();
        setRequisitions(data || []);
      }
    } catch (error) {
      console.error('Error fetching requisitions:', error);
    } finally {
      setLoadingRequisitions(false);
    }
  };

  const fetchReturns = async () => {
    if (!order?.production_order_id) return;
    setLoadingReturns(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/material-returns?production_order_id=${order.production_order_id}&limit=100`));
      if (response.ok) {
        setReturns(await response.json());
      }
    } catch (error) {
      console.error('Error fetching returns:', error);
    } finally {
      setLoadingReturns(false);
    }
  };

  const fetchSelectedReturnDetails = async (returnId) => {
    if (selectedReturnId === returnId) {
      setSelectedReturnId(null);
      setSelectedReturnDetails(null);
      return;
    }
    setSelectedReturnId(returnId);
    setSelectedReturnDetails(null);
    setLoadingReturnDetails(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/material-returns/${returnId}`));
      if (response.ok) {
        setSelectedReturnDetails(await response.json());
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingReturnDetails(false);
    }
  };

  const fetchSelectedRequisitionDetails = async (requisitionId) => {
    if (selectedRequisitionId === requisitionId) {
      setSelectedRequisitionId(null);
      setSelectedRequisitionDetails(null);
      return;
    }
    setSelectedRequisitionId(requisitionId);
    setSelectedRequisitionDetails(null);
    setLoadingRequisitionDetails(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/inventory/requisitions/${requisitionId}/details`));
      if (response.ok) {
        setSelectedRequisitionDetails(await response.json());
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingRequisitionDetails(false);
    }
  };

  const fetchOrderTasks = async () => {
    if (!order?.production_order_id || orderTasks !== null) return;
    try {
      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/by-order/${order.production_order_id}`));
      setOrderTasks(response.ok ? await response.json() : []);
    } catch {
      setOrderTasks([]);
    }
  };

  const fetchAvailableMaterials = async () => {
    if (!order?.production_order_id) return;

    try {
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/production-order/${order.production_order_id}/available-items`));
      if (response.ok) {
        const materials = await response.json();
        setAvailableMaterials(materials || []);
      }
    } catch (error) {
      console.error('Error fetching BOM materials:', error);
    }
  };

  const handleOpenCreateRequisition = () => {
    fetchAvailableMaterials();
    setRequisitionItems([{ inventory_item_id: '', requested_quantity: '', notes: '' }]);
    setShowCreateRequisitionModal(true);
  };

  const handleAddRequisitionItem = () => {
    setRequisitionItems([...requisitionItems, { inventory_item_id: '', requested_quantity: '', notes: '' }]);
  };

  const handleRemoveRequisitionItem = (index) => {
    setRequisitionItems(requisitionItems.filter((_, i) => i !== index));
  };

  const handleRequisitionItemChange = (index, field, value) => {
    const updated = [...requisitionItems];
    updated[index][field] = value;
    setRequisitionItems(updated);
  };

  const handleSubmitRequisition = async () => {
    const validItems = requisitionItems.filter(item => item.inventory_item_id && item.requested_quantity > 0);

    if (validItems.length === 0) {
      showError(t('messages.validationError'), t('messages.validationNoMaterials'));
      return;
    }

    if (!order?.production_order_id) {
      showError(t('messages.validationError'), t('messages.validationNoOrderId'));
      return;
    }

    try {
      const requestBody = {
        production_order_id: order.production_order_id,
        priority: 'NORMAL',
        notes: 'Material request from production operator',
        items: validItems.map(item => ({
          inventory_item_id: parseInt(item.inventory_item_id),
          requested_quantity: parseFloat(item.requested_quantity),
          notes: item.notes || null
        }))
      };

      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/create?requested_by=${user?.user_id || 1}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('messages.requisitionCreated'), t('viewProductionOrder.requisitionCreatedMessage', { number: result.requisition_number }));
        setShowCreateRequisitionModal(false);
        fetchRequisitions();
      } else {
        await showApiError(response, showError, tCommon, t('messages.failedToCreateRequisition'));
      }
    } catch (error) {
      console.error('Error creating requisition:', error);
      showError(t('messages.failedToCreateRequisition'), t('viewProductionOrder.errors.createRequisitionError'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('messages.deleteConfirmation'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/production/orders/${order?.po_number || orderId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('messages.productionOrderDeleted'), t('messages.productionOrderDeletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon, t('messages.failedToDeleteOrder'));
      }
    } catch (error) {
      console.error('Error deleting production order:', error);
      setError(t('viewProductionOrder.errors.deleteError'));
      showError(t('messages.failedToDeleteOrder'), t('viewProductionOrder.errors.deleteError'));
    }
  };

  const renderOverviewTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('sections.orderInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('fields.poNumber')} *</label>
            <input
              type="text"
              value={editData.po_number || ''}
              onChange={(e) => handleFieldChange('po_number', e.target.value)}
              disabled={true}  // PO Number should never be editable
              required
            />
          </div>
          <div className="form-group">
            <label>{t('fields.customerName')} *</label>
            <input
              type="text"
              value={editData.customer_name || ''}
              onChange={(e) => handleFieldChange('customer_name', e.target.value)}
              disabled={true}  // Customer info should come from customer record
              required
            />
          </div>
          <div className="form-group">
            <label>{t('fields.quotationId')}</label>
            {editData.quotation_id ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <EntityLink
                  type="quote"
                  id={editData.quotation_id}
                  label={editData.quotation_number || `QUO-${editData.quotation_id}`}
                  returnLabel={editData.po_number}
                  returnType="production-order"
                  returnId={editData.po_number || orderId}
                />
              </div>
            ) : (
              <span className="form-value" style={{ color: 'var(--text-secondary)' }}>—</span>
            )}
          </div>
          <div className="form-group">
            <label>{t('fields.salesOrderId')}</label>
            {editData.sales_order_id ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <EntityLink
                  type="sales-order"
                  id={editData.sales_order_id}
                  label={editData.sales_order_number || `SO-${editData.sales_order_id}`}
                  returnLabel={editData.po_number}
                  returnType="production-order"
                  returnId={editData.po_number || orderId}
                />
              </div>
            ) : (
              <span className="form-value" style={{ color: 'var(--text-secondary)' }}>—</span>
            )}
          </div>
          <div className="form-group">
            <label>{t('fields.customerEmail')}</label>
            <input
              type="email"
              value={editData.customer_email || ''}
              onChange={(e) => handleFieldChange('customer_email', e.target.value)}
              disabled={true}  // Customer info should come from customer record
            />
          </div>
          <div className="form-group">
            <label>{t('fields.customerPhone')}</label>
            <input
              type="tel"
              value={editData.customer_phone || ''}
              onChange={(e) => handleFieldChange('customer_phone', e.target.value)}
              disabled={true}  // Customer info should come from customer record
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('sections.productionDetails')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('fields.priority')}</label>
            <select
              value={editData.priority || 'NORMAL'}
              onChange={(e) => handleFieldChange('priority', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="LOW">{t('priority.low')}</option>
              <option value="NORMAL">{t('priority.normal')}</option>
              <option value="HIGH">{t('priority.high')}</option>
              <option value="URGENT">{t('priority.urgent')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('fields.status')}</label>
            <select
              value={editData.status || 'PLANNED'}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="PLANNED">{t('status.planned')}</option>
              <option value="RELEASED">{t('status.released')}</option>
              <option value="IN_PROGRESS">{t('status.inProgress')}</option>
              <option value="ON_HOLD">{t('status.onHold')}</option>
              <option value="COMPLETED">{t('status.completed')}</option>
              <option value="CANCELLED">{t('status.cancelled')}</option>
            </select>
          </div>
          {/* Operator field commented out - not in backend model yet
          <div className="form-group">
            <label>Assigned Operator</label>
            <select
              value={editData.assigned_operator_id || ''}
              onChange={(e) => handleFieldChange('assigned_operator_id', e.target.value)}
              disabled={!editMode && !createMode}
            >
              <option value="">Not Assigned</option>
              {operators.map(operator => (
                <option key={operator.user_id} value={operator.user_id}>
                  {operator.full_name || `${operator.first_name} ${operator.last_name}` || operator.username}
                </option>
              ))}
            </select>
          </div>
          */}
        </div>
      </div>
    </div>
  );

  const renderScheduleTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('sections.datesSchedule')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('fields.createdOn')}</label>
            <span className="form-value">
              {editData.created_on ? formatAppDate(editData.created_on) : t('messages.notSet')}
            </span>
          </div>
          <div className="form-group">
            <label>{t('fields.plannedDueDate')} *</label>
            {editMode || createMode ? (
              <input
                type="date"
                value={editData.planned_due_date || ''}
                onChange={(e) => handleFieldChange('planned_due_date', e.target.value)}
                required
              />
            ) : (
              <span className="form-value">
                {editData.planned_due_date ? formatAppDate(editData.planned_due_date) : t('messages.notSet')}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>{t('fields.plannedStartDate')}</label>
            {editMode || createMode ? (
              <input
                type="date"
                value={editData.planned_start_date || ''}
                onChange={(e) => handleFieldChange('planned_start_date', e.target.value)}
              />
            ) : (
              <span className="form-value">
                {editData.planned_start_date ? formatAppDate(editData.planned_start_date) : t('messages.notSet')}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>{t('fields.actualStartDate')}</label>
            {editMode || createMode ? (
              <input
                type="date"
                value={editData.actual_start_date || ''}
                onChange={(e) => handleFieldChange('actual_start_date', e.target.value)}
              />
            ) : (
              <span className="form-value">
                {editData.actual_start_date ? formatAppDate(editData.actual_start_date) : t('messages.notSet')}
              </span>
            )}
          </div>
          <div className="form-group">
            <label>{t('fields.actualCompletionDate')}</label>
            {editMode || createMode ? (
              <input
                type="date"
                value={editData.actual_completion_date || ''}
                onChange={(e) => handleFieldChange('actual_completion_date', e.target.value)}
              />
            ) : (
              <span className="form-value">
                {editData.actual_completion_date ? formatAppDate(editData.actual_completion_date) : t('messages.notSet')}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>{t('sections.quantitiesProgress')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('fields.totalQuantity')}</label>
            <input
              type="number"
              value={(() => {
                const total = (editData.items || []).reduce((sum, item) =>
                  sum + (parseFloat(item.order_quantity) || 0), 0);
                return total;
              })()}
              disabled={true}
              min="0"
              title={t('viewProductionOrder.titles.sumQuantities')}
            />
          </div>
          <div className="form-group">
            <label>{t('fields.completedQuantity')}</label>
            <input
              type="number"
              value={(() => {
                const completed = (editData.items || []).reduce((sum, item) =>
                  sum + (parseFloat(item.produced_quantity) || 0), 0);
                return completed;
              })()}
              disabled={true}
              min="0"
              title={t('viewProductionOrder.titles.sumCompleted')}
            />
          </div>
          <div className="form-group">
            <label>{t('fields.defectiveQuantity')}</label>
            <input
              type="number"
              value={0}
              disabled={true}
              min="0"
              title={t('viewProductionOrder.titles.defectiveNotImplemented')}
            />
          </div>
          <div className="form-group">
            <label>{t('fields.progress')}</label>
            <input
              type="number"
              value={(() => {
                const total = (editData.items || []).reduce((sum, item) =>
                  sum + (parseFloat(item.order_quantity) || 0), 0);
                const completed = (editData.items || []).reduce((sum, item) =>
                  sum + (parseFloat(item.produced_quantity) || 0), 0);
                return total > 0 ? Math.round((completed / total) * 100) : 0;
              })()}
              disabled={true}
              min="0"
              max="100"
              title={t('viewProductionOrder.titles.progressCalculated')}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderCostsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('sections.costAnalysis')}</h3>
        <div className="form-grid">
          <div className="form-group">
            {/* Both are calculated when the order is read (#379): not editable */}
            <label>{t('fields.estimatedCost')}</label>
            <input type="number" value={editData.total_estimated_cost || 0} disabled={true} />
          </div>
          <div className="form-group">
            <label>{t('fields.actualCost')}</label>
            <input type="number" value={editData.total_actual_cost || 0} disabled={true} />
          </div>
        </div>
        <p className="field-hint">{t('viewProductionOrder.costsHint')}</p>
      </div>
    </div>
  );

  const renderItemsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('sections.productionLineItems')}</h3>
        <div className="items-table">
          {(!editData.items || editData.items.length === 0) ? (
            <div className="no-data">{t('messages.noLineItems')}</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t('tableHeaders.lineNumber')}</th>
                  <th>{t('tableHeaders.productCode')}</th>
                  <th>{t('tableHeaders.productName')}</th>
                  <th>{t('tableHeaders.description')}</th>
                  <th>{t('tableHeaders.quantity')}</th>
                  <th>{t('tableHeaders.unitOfMeasure')}</th>
                  <th>{t('tableHeaders.completed')}</th>
                  <th>{t('tableHeaders.progress')}</th>
                  <th>{t('tableHeaders.workflow')}</th>
                  <th>{t('tableHeaders.unitPrice')}</th>
                  <th>{t('tableHeaders.total')}</th>
                  <th>{t('tableHeaders.status')}</th>
                </tr>
              </thead>
              <tbody>
                {editData.items.map((item, idx) => {
                  const completionPercentage = item.completion_percentage ||
                    (item.order_quantity > 0 ? Math.round((item.produced_quantity || 0) / item.order_quantity * 100) : 0);

                  return (
                    <tr key={item.production_order_line_id || item.id || idx}>
                      <td>{item.line_number || idx + 1}</td>
                      <td>{item.product_code || 'N/A'}</td>
                      <td>{item.product_name || 'N/A'}</td>
                      <td>{item.description || 'N/A'}</td>
                      <td className="text-right">{item.order_quantity?.toLocaleString() || '0'}</td>
                      <td className="text-center">{item.unit_of_measure || 'PCS'}</td>
                      <td className="text-right">{item.produced_quantity?.toLocaleString() || '0'}</td>
                      <td>
                        <div className="progress-bar-mini">
                          <div
                            className="progress-fill-mini"
                            style={{ width: `${completionPercentage}%` }}
                          />
                          <span className="progress-text-mini">{completionPercentage}%</span>
                        </div>
                      </td>
                      <td>
                        {item.workflow_instance_id ? (
                          <span className="workflow-info">
                            WF-{item.workflow_instance_id}
                          </span>
                        ) : (
                          <span className="text-secondary">{t('messages.noWorkflow')}</span>
                        )}
                      </td>
                      <td className="text-right">${item.unit_price?.toFixed(2) || '0.00'}</td>
                      <td className="text-right">${item.line_total?.toFixed(2) || '0.00'}</td>
                      <td>
                        <span className={`status-badge status-${item.status?.toLowerCase() || 'pending'}`}>
                          {item.status || 'PENDING'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );

  const renderRequisitionsTab = () => {
    return (
      <div className="tab-content">
        <div className="form-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>{t('sections.materialRequisitions')}</h3>
            {!createMode && (
              <button className="btn btn-primary" onClick={handleOpenCreateRequisition}>
                {t('modals.requestMaterials')}
              </button>
            )}
          </div>

          {loadingRequisitions ? (
            <div className="loading">{t('messages.loadingRequisitions')}</div>
          ) : requisitions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              <p>{t('messages.noRequisitions')}</p>
              <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                {t('messages.noRequisitionsHint')}
              </p>
            </div>
          ) : (
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>{t('tableHeaders.requisitionNumber')}</th>
                  <th>{t('tableHeaders.requestedDate')}</th>
                  <th>{t('tableHeaders.status')}</th>
                  <th>{t('fields.priority')}</th>
                  <th>{t('tableHeaders.requestedBy')}</th>
                  <th>{t('tableHeaders.approvedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {requisitions.map(req => (
                  <React.Fragment key={req.requisition_id}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      onClick={() => fetchSelectedRequisitionDetails(req.requisition_id)}
                    >
                      <td>
                        <button
                          className="entity-link"
                          style={{ fontWeight: 600 }}
                          onClick={e => { e.stopPropagation(); fetchSelectedRequisitionDetails(req.requisition_id); }}
                        >
                          {req.requisition_number} {selectedRequisitionId === req.requisition_id ? '▲' : '▼'}
                        </button>
                      </td>
                      <td>{formatAppDate(req.requested_date)}</td>
                      <td>
                        <span className={`status-badge ${
                          req.status === 'PENDING' ? 'status-warning' :
                          req.status === 'APPROVED' ? 'status-success' :
                          req.status === 'PICKED' ? 'status-in-progress' :
                          req.status === 'ISSUED' ? 'status-verified' :
                          req.status === 'REJECTED' ? 'status-error' : 'status-inactive'
                        }`}>
                          {req.status}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${
                          req.priority === 'URGENT' ? 'status-error' :
                          req.priority === 'HIGH' ? 'status-warning' : 'status-secondary'
                        }`}>
                          {req.priority}
                        </span>
                      </td>
                      <td>{req.requested_by_name || t('viewProductionOrder.userFallback', { id: req.requested_by })}</td>
                      <td>{req.approved_by_name || '-'}</td>
                    </tr>
                    {selectedRequisitionId === req.requisition_id && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0, background: 'var(--bg-secondary)' }}>
                          {loadingRequisitionDetails ? (
                            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('viewProductionOrder.loadingDetails')}</div>
                          ) : selectedRequisitionDetails ? (
                            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)' }}>
                              {selectedRequisitionDetails.notes && (
                                <p style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                  <strong>{tCommon('ui.notes')}:</strong> {selectedRequisitionDetails.notes}
                                </p>
                              )}
                              {selectedRequisitionDetails.is_over_bom && (
                                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '8px 12px', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                                  <strong>⚠ {t('viewProductionOrder.overBomRequest')}:</strong> {selectedRequisitionDetails.over_bom_reason || t('viewProductionOrder.noReasonProvided')}
                                </div>
                              )}
                              <table className="data-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                <thead>
                                  <tr>
                                    <th>{t('viewProductionOrder.columns.item')}</th>
                                    <th>{tCommon('ui.code')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('viewProductionOrder.columns.requested')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('viewProductionOrder.columns.approvedQty')}</th>
                                    <th>{t('tableHeaders.unitOfMeasure')}</th>
                                    <th>{tCommon('ui.notes')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(selectedRequisitionDetails.items || []).map((item, i) => (
                                    <tr key={i}>
                                      <td>{item.item_name}</td>
                                      <td style={{ color: 'var(--text-secondary)' }}>{item.item_code}</td>
                                      <td style={{ textAlign: 'right' }}>{parseFloat(item.requested_quantity).toLocaleString()}</td>
                                      <td style={{ textAlign: 'right' }}>{item.approved_quantity != null ? parseFloat(item.approved_quantity).toLocaleString() : '-'}</td>
                                      <td>{item.unit_of_measure || '-'}</td>
                                      <td style={{ color: 'var(--text-secondary)' }}>{item.notes || '-'}</td>
                                    </tr>
                                  ))}
                                  {(!selectedRequisitionDetails.items || selectedRequisitionDetails.items.length === 0) && (
                                    <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{t('viewProductionOrder.noItemDetails')}</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Material Returns */}
        <div className="form-section" style={{ marginTop: '1.5rem' }}>
          <h3>{t('viewProductionOrder.materialReturns')}</h3>
          {loadingReturns ? (
            <div className="loading">{t('viewProductionOrder.loadingReturns')}</div>
          ) : returns.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              {t('viewProductionOrder.noReturns')}
            </div>
          ) : (
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>{t('viewProductionOrder.columns.returnNumber')}</th>
                  <th>{tCommon('ui.date')}</th>
                  <th>{t('tableHeaders.status')}</th>
                  <th>{t('viewProductionOrder.columns.requisition')}</th>
                  <th>{t('viewProductionOrder.columns.returnedBy')}</th>
                  <th>{t('viewProductionOrder.columns.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {returns.map(ret => (
                  <React.Fragment key={ret.return_id}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      onClick={() => fetchSelectedReturnDetails(ret.return_id)}
                    >
                      <td>
                        <button
                          className="entity-link"
                          style={{ fontWeight: 600 }}
                          onClick={e => { e.stopPropagation(); fetchSelectedReturnDetails(ret.return_id); }}
                        >
                          {ret.return_number} {selectedReturnId === ret.return_id ? '▲' : '▼'}
                        </button>
                      </td>
                      <td>{ret.return_date ? formatAppDate(ret.return_date) : '-'}</td>
                      <td>
                        <span className={`status-badge ${
                          ret.status === 'PENDING'   ? 'status-warning' :
                          ret.status === 'RECEIVED'  ? 'status-in-progress' :
                          ret.status === 'ACCEPTED'  ? 'status-success' :
                          ret.status === 'RESTOCKED' ? 'status-verified' :
                          ret.status === 'REJECTED'  ? 'status-error' :
                          ret.status === 'CANCELLED' ? 'status-inactive' : 'status-secondary'
                        }`}>
                          {ret.status}
                        </span>
                      </td>
                      <td>{ret.requisition_number || '-'}</td>
                      <td>{ret.returned_by_name || '-'}</td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{ret.return_reason || '-'}</td>
                    </tr>
                    {selectedReturnId === ret.return_id && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0, background: 'var(--bg-secondary)' }}>
                          {loadingReturnDetails ? (
                            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('viewProductionOrder.loadingDetails')}</div>
                          ) : selectedReturnDetails ? (
                            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)' }}>
                              {selectedReturnDetails.inspection_notes && (
                                <p style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                  <strong>{t('viewProductionOrder.inspectionNotes')}:</strong> {selectedReturnDetails.inspection_notes}
                                </p>
                              )}
                              {selectedReturnDetails.rejection_reason && (
                                <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '8px 12px', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                                  <strong>{t('viewProductionOrder.rejectionReason')}:</strong> {selectedReturnDetails.rejection_reason}
                                </div>
                              )}
                              <table className="data-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                                <thead>
                                  <tr>
                                    <th>{t('viewProductionOrder.columns.item')}</th>
                                    <th>{tCommon('ui.code')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('viewProductionOrder.columns.returned')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('viewProductionOrder.columns.accepted')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('viewProductionOrder.columns.rejected')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('viewProductionOrder.columns.missing')}</th>
                                    <th>{t('viewProductionOrder.columns.condition')}</th>
                                    <th>{t('tableHeaders.unitOfMeasure')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(selectedReturnDetails.items || []).map((item, i) => (
                                    <tr key={i}>
                                      <td>{item.item_name}</td>
                                      <td style={{ color: 'var(--text-secondary)' }}>{item.item_code}</td>
                                      <td style={{ textAlign: 'right' }}>{parseFloat(item.quantity_returned).toLocaleString()}</td>
                                      <td style={{ textAlign: 'right' }}>{item.quantity_accepted != null ? parseFloat(item.quantity_accepted).toLocaleString() : '-'}</td>
                                      <td style={{ textAlign: 'right' }}>{item.quantity_rejected != null ? parseFloat(item.quantity_rejected).toLocaleString() : '-'}</td>
                                      <td style={{ textAlign: 'right', ...(parseFloat(item.quantity_missing) > 0 ? { color: 'var(--warning)', fontWeight: 600 } : {}) }}>{item.quantity_missing != null ? parseFloat(item.quantity_missing).toLocaleString() : '-'}</td>
                                      <td>{item.item_condition || item.condition || '-'}</td>
                                      <td>{item.unit_of_measure || '-'}</td>
                                    </tr>
                                  ))}
                                  {(!selectedReturnDetails.items || selectedReturnDetails.items.length === 0) && (
                                    <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>{t('viewProductionOrder.noItemDetails')}</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderNotesTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('sections.instructionsNotes')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('fields.specialInstructions')}</label>
            <textarea
              value={editData.special_instructions || ''}
              onChange={(e) => handleFieldChange('special_instructions', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('placeholders.specialInstructions')}
              rows="3"
            />
          </div>
          <div className="form-group full-width">
            <label>{t('fields.internalNotes')}</label>
            <textarea
              value={editData.notes || ''}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('placeholders.internalNotes')}
              rows="3"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTasksTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('viewProductionOrder.productionTasks')}</h3>
        {orderTasks === null ? (
          <div className="loading">{t('viewProductionOrder.loadingTasks')}</div>
        ) : orderTasks.length === 0 ? (
          <div className="no-data" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('viewProductionOrder.noTasks')}</div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('viewProductionOrder.columns.task')}</th>
                <th>{tCommon('ui.type')}</th>
                <th>{t('tableHeaders.status')}</th>
                <th>{t('viewProductionOrder.columns.operator')}</th>
                <th>{t('viewProductionOrder.columns.output')}</th>
                <th>{t('viewProductionOrder.columns.wasteKg')}</th>
                <th>{t('viewProductionOrder.columns.rolls')}</th>
              </tr>
            </thead>
            <tbody>
              {orderTasks.map((task, i) => (
                <tr
                  key={task.task_id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tasks/${task.task_id}`, { state: { returnTo: {
                    label: order?.po_number,
                    orderId: order?.po_number || order?.production_order_id,
                    page: 'production-order',
                    detailId: order?.po_number || order?.production_order_id,
                    innerTab: 'tasks',
                    targetType: 'task',
                    targetId: task.task_id,
                  } } })}
                >
                  <td>{i + 1}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{task.task_code}</span>
                    <br /><span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{task.task_name}</span>
                  </td>
                  <td><span style={{ fontSize: '0.8rem' }}>{task.task_type}</span></td>
                  <td><span className={`status-badge status-${task.status?.toLowerCase()}`}>{task.status}</span></td>
                  <td>{task.operator_name || '-'}</td>
                  <td>
                    {task.total_output > 0
                      ? <>{parseFloat(task.total_output).toLocaleString(undefined, { maximumFractionDigits: 3 })} <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{task.output_unit || ''}</span></>
                      : '-'}
                  </td>
                  <td>{task.total_waste_kg > 0 ? <>{parseFloat(task.total_waste_kg).toFixed(3)} <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>kg</span></> : '-'}</td>
                  <td>
                    {task.task_type === 'QUALITY_CHECK' ? (
                      <span className={`status-badge ${task.status === 'COMPLETED' ? 'status-success' : 'status-warning'}`}>
                        {task.status === 'COMPLETED' ? t('viewProductionOrder.qcDone') : t('viewProductionOrder.qcPending')}
                      </span>
                    ) : (
                      task.rolls_count > 0 ? task.rolls_count : '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="view-container"><div className="loading">{t('messages.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!order && !createMode) {
    return <div className="view-container"><div className="loading">{t('messages.noOrderFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <BackButton onBack={onBack} entityType="production-order" entityIds={[orderId, editData?.production_order_id, editData?.po_number]} />
          <h1 style={{ fontSize: '1rem' }}>
            {createMode
              ? t('toolbar.titles.newProductionOrder')
              : [editData.po_number, editData.customer_name].filter(Boolean).join(' — ') || t('toolbar.titles.viewProductionOrder')}
          </h1>
          {!createMode && editData.status && (
            <span className={`status-badge ${
              editData.status === 'PENDING' ? 'status-pending' :
              editData.status === 'CONFIRMED' ? 'status-confirmed' :
              editData.status === 'IN_PROGRESS' ? 'status-in-progress' :
              editData.status === 'COMPLETED' ? 'status-completed' :
              editData.status === 'ON_HOLD' ? 'status-on-hold' :
              editData.status === 'CANCELLED' ? 'status-cancelled' : 'status-inactive'
            }`}>
              {editData.status === 'IN_PROGRESS' ? t('status.inProgress') :
               editData.status === 'ON_HOLD' ? t('status.onHold') :
               t(`status.${editData.status.toLowerCase()}`)}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              {order?.status === 'PLANNED' && (
                <button className="btn-success" onClick={handleReleaseClick}>{t('toolbar.buttons.release')}</button>
              )}
              {isAdmin && order?.status && !['COMPLETED', 'CANCELLED'].includes(order.status) && (
                <button className="btn-danger" onClick={handleForceClose}>{t('viewProductionOrder.forceClose.button')}</button>
              )}
              <button className="btn-secondary" onClick={onEdit}>{t('toolbar.buttons.edit')}</button>
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('toolbar.buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? t('toolbar.buttons.create') : t('toolbar.buttons.save')}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            {t('viewTabs.overview')}
          </button>
          <button
            className={`tab-item ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            {t('viewTabs.schedule')}
          </button>
          <button
            className={`tab-item ${activeTab === 'costs' ? 'active' : ''}`}
            onClick={() => setActiveTab('costs')}
          >
            {t('viewTabs.costs')}
          </button>
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'items' ? 'active' : ''}`}
              onClick={() => setActiveTab('items')}
            >
              {t('viewTabs.items')}
            </button>
          )}
          <button
            className={`tab-item ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            {t('viewTabs.notes')}
          </button>
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'requisitions' ? 'active' : ''}`}
              onClick={() => setActiveTab('requisitions')}
            >
              {t('viewTabs.requisitions')}
            </button>
          )}
          {!createMode && (
            <button
              className={`tab-item ${activeTab === 'tasks' ? 'active' : ''}`}
              onClick={() => { setActiveTab('tasks'); fetchOrderTasks(); }}
            >
              {t('viewProductionOrder.tabs.tasks')}
            </button>
          )}
          {!createMode && canSeeVariance && (
            <button
              className={`tab-item ${activeTab === 'variance' ? 'active' : ''}`}
              onClick={() => setActiveTab('variance')}
            >
              {t('viewProductionOrder.tabs.bomVariance')}
            </button>
          )}
        </div>

        <div className="tab-panels">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'schedule' && renderScheduleTab()}
          {activeTab === 'costs' && renderCostsTab()}
          {activeTab === 'items' && renderItemsTab()}
          {activeTab === 'notes' && renderNotesTab()}
          {activeTab === 'requisitions' && renderRequisitionsTab()}
          {activeTab === 'tasks' && renderTasksTab()}
          {activeTab === 'variance' && canSeeVariance && order?.production_order_id && (
            <div className="tab-content">
              <div className="form-section">
                <h3>{t('bomVariance.title')}</h3>
                <VarianceDetail productionOrderId={order.production_order_id} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Requisition Modal */}
      {showCreateRequisitionModal && (
        <div className="modal-overlay" onClick={() => setShowCreateRequisitionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>{t('viewProductionOrder.requestModal.title')}</h2>
              <button className="modal-close" onClick={() => setShowCreateRequisitionModal(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '1rem' }}>
                <p><strong>{t('viewProductionOrder.requestModal.productionOrder')}:</strong> {order?.po_number}</p>
                <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  {t('viewProductionOrder.requestModal.description')}
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <strong>{t('modals.materialsRequested')}</strong>
                  <button className="btn btn-sm btn-secondary" onClick={handleAddRequisitionItem}>
                    {t('modals.addMaterial')}
                  </button>
                </div>

                {requisitionItems.map((item, index) => {
                  const selectedMaterial = availableMaterials.find(m => m.inventory_item_id === parseInt(item.inventory_item_id));
                  const maxQty = selectedMaterial ? parseFloat(selectedMaterial.max_quantity) : null;

                  return (
                    <div key={index} style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 2fr 40px',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: '4px'
                    }}>
                      <div>
                        <select
                          value={item.inventory_item_id}
                          onChange={(e) => handleRequisitionItemChange(index, 'inventory_item_id', e.target.value)}
                          style={{
                            padding: '0.5rem',
                            borderRadius: '4px',
                            border: '1px solid var(--border)',
                            background: 'var(--bg-primary)',
                            color: 'var(--text-primary)',
                            width: '100%'
                          }}
                        >
                          <option value="" disabled hidden>{t('placeholders.selectMaterial')}</option>
                          {availableMaterials.map(mat => (
                            <option key={mat.inventory_item_id} value={mat.inventory_item_id}>
                              {mat.item_code} - {mat.item_name}
                            </option>
                          ))}
                        </select>
                        {selectedMaterial && (
                          <div style={{ fontSize: '0.75em', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                            {t('viewProductionOrder.requestModal.bomMax', { perUnit: selectedMaterial.quantity_per_unit, orderQty: selectedMaterial.order_quantity, max: maxQty?.toFixed(3), uom: selectedMaterial.unit_of_measure })}
                            {selectedMaterial.alternative_group && (
                              <div>{t('viewProductionOrder.requestModal.alternativeOf', { items: availableMaterials
                                .filter(m => m.alternative_group === selectedMaterial.alternative_group && m.inventory_item_id !== selectedMaterial.inventory_item_id)
                                .map(m => m.item_code).join(', ') })}</div>
                            )}
                          </div>
                        )}
                      </div>

                      <input
                        type="number"
                        placeholder={t('placeholders.quantity')}
                        value={item.requested_quantity}
                        onChange={(e) => handleRequisitionItemChange(index, 'requested_quantity', e.target.value)}
                        max={maxQty || undefined}
                        step="0.001"
                        min="0"
                        style={{
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      />

                      <input
                        type="text"
                        placeholder={t('placeholders.notesOptional')}
                        value={item.notes}
                        onChange={(e) => handleRequisitionItemChange(index, 'notes', e.target.value)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      />

                      {requisitionItems.length > 1 && (
                        <button
                          onClick={() => handleRemoveRequisitionItem(index)}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            padding: '0.5rem'
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateRequisitionModal(false)}>
                {t('toolbar.buttons.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSubmitRequisition}>
                {t('modals.submitRequisition')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Release Confirmation Dialog */}
      {showReleaseConfirmDialog && (
        <div className="modal-overlay" onClick={() => setShowReleaseConfirmDialog(false)}>
          <div className="receipt-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>{t('modals.confirmRelease')}</h3>
              <button className="btn-close" onClick={() => setShowReleaseConfirmDialog(false)}>✕</button>
            </div>

            <div className="dialog-content">
              <div className="receipt-summary">
                <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
                  {t('modals.confirmReleaseMessage')} <strong>{order?.po_number}</strong>?
                </p>
                <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '8px' }}>
                  {t('modals.releaseActions')}
                </p>
                <ul style={{ fontSize: '13px', color: '#94a3b8', lineHeight: '1.8', paddingLeft: '20px' }}>
                  <li>{t('modals.releaseAction1')}</li>
                  <li>{t('modals.releaseAction2')}</li>
                  <li>{t('modals.releaseAction3')}</li>
                  <li>{t('modals.releaseAction4')}</li>
                </ul>
              </div>
            </div>

            <div className="dialog-footer">
              <button className="btn-cancel" onClick={() => setShowReleaseConfirmDialog(false)}>
                {t('toolbar.buttons.cancel')}
              </button>
              <button className="btn-submit" onClick={handleReleaseConfirm}>
                {t('modals.confirmReleaseButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ViewProductionOrder.displayName = 'ViewProductionOrder';

export default ViewProductionOrder;